import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, Plus, Upload, Sparkles, Flag, CalendarClock, FileWarning, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import KpiCard from "@/components/dashboard/KpiCard";
import StageKpiCard from "@/components/dashboard/StageKpiCard";
import DateRangeFilter from "@/components/dashboard/DateRangeFilter";
import ActivityFeedCard from "@/components/dashboard/ActivityFeedCard";
import WeeklyCalendarCard from "@/components/dashboard/WeeklyCalendarCard";
import UpcomingDeadlinesCard, { CriticalAlertBanner } from "@/components/dashboard/UpcomingDeadlinesCard";
import UpcomingEventsCard from "@/components/dashboard/UpcomingEventsCard";
import ExpiringDocumentsCard from "@/components/dashboard/ExpiringDocumentsCard";
import AthleteScopeHeader from "@/components/dashboard/AthleteScopeHeader";
import AthleteJourneyCard from "@/components/dashboard/AthleteJourneyCard";
import DashboardIntelligenceCard from "@/components/dashboard/DashboardIntelligenceCard";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState";
import PlayerSheet from "@/components/PlayerSheet";
import AddPlayerDialog from "@/components/AddPlayerDialog";
import EventDialog from "@/components/EventDialog";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, tf, lang, locale } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = searchParams.get("range") || "7d";
  const playerId = searchParams.get("player") || null;
  const from = searchParams.get("from") || null;
  const to = searchParams.get("to") || null;

  const [overview, setOverview] = useState(null);
  const [dash, setDash] = useState(null);
  const [docs, setDocs] = useState(null);
  const [deadlines, setDeadlines] = useState(null);
  const [openPlayer, setOpenPlayer] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Latest-selection-wins: stale responses from a previous athlete are discarded.
  const playerIdRef = useRef(playerId);
  const prevPlayerRef = useRef(playerId);
  useEffect(() => {
    playerIdRef.current = playerId;
    if (prevPlayerRef.current !== playerId) {
      prevPlayerRef.current = playerId;
      setOverview(null); setDash(null); setDocs(null);
    }
  }, [playerId]);

  const clearAthlete = useCallback(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("player");
    setSearchParams(p);
  }, [searchParams, setSearchParams]);

  const loadOverview = useCallback(async () => {
    const pid = playerId;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Paris";
      const params = { range, tz, player_id: pid || undefined, date_from: from || undefined, date_to: to || undefined };
      const r = await api.get("/dashboard/overview", { params });
      if (playerIdRef.current !== pid) return;
      setOverview(r.data);
    } catch (e) {
      if (playerIdRef.current !== pid) return;
      if (pid && e?.response?.status === 404) {
        // Athlete unavailable (removed, demo toggled, unauthorized): fall back safely.
        clearAthlete();
        return;
      }
      setOverview({ error: true });
    }
  }, [range, playerId, from, to, clearAthlete]);

  const loadDash = useCallback(async () => {
    const pid = playerId;
    try {
      const r = await api.get("/dashboard", { params: { player_id: pid || undefined } });
      if (playerIdRef.current !== pid) return;
      setDash(r.data);
    } catch { if (playerIdRef.current === pid) setDash({ error: true }); }
  }, [playerId]);

  const loadDocs = useCallback(async () => {
    const pid = playerId;
    try {
      const r = await api.get("/documents", { params: { days: 30, limit: 200, player_id: pid || undefined } });
      if (playerIdRef.current !== pid) return;
      setDocs(r.data.items || []);
    } catch { if (playerIdRef.current === pid) setDocs([]); }
  }, [playerId]);

  const loadDeadlines = useCallback(async () => {
    try {
      const r = await api.get("/dashboard/deadlines");
      setDeadlines(r.data);
    } catch { setDeadlines({ items: [], critical: [] }); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadDash(); loadDocs(); }, [loadDash, loadDocs]);
  useEffect(() => { loadDeadlines(); }, [loadDeadlines]);

  const reloadAll = () => { loadOverview(); loadDash(); loadDocs(); loadDeadlines(); };

  const setRange = ({ range: r, from: f, to: t }) => {
    const p = new URLSearchParams(searchParams);
    p.set("range", r);
    if (f) p.set("from", f); else p.delete("from");
    if (t) p.set("to", t); else p.delete("to");
    setSearchParams(p, { replace: true });
  };

  const seed = async () => {
    if (!window.confirm(t("dash.confirmSeed"))) return;
    setSeeding(true);
    try { await api.post("/seed"); toast.success(t("dash.demoLoaded")); reloadAll(); }
    catch { toast.error(t("dash.seedError")); }
    finally { setSeeding(false); }
  };

  const k = overview?.kpis;
  const athlete = overview?.athlete || null;
  const athleteMode = !!playerId;
  const totals = overview?.totals || dash?.totals || {};
  const totalPlayers = overview?.total_players ?? Object.values(totals).reduce((a, b) => a + b, 0);
  const isEmpty = overview && !overview.error && totalPlayers === 0 && !playerId;
  const rangeLabel = t(`dash.range.${["today", "yesterday", "7d", "30d", "month"].includes(range) ? range : "custom"}`);

  const firstName = athlete?.name?.split(" ")[0] || "";
  const nd = athlete?.next_deadline || null;
  const ndDate = nd ? new Date(nd.at).toLocaleDateString(locale, { day: "numeric", month: "short" }) : null;
  const docsAttention = docs || [];
  const docsExpired = docsAttention.filter((d) => (d.days_left ?? 1) < 0).length;
  const sinceDays = athlete?.journey?.since_days;

  return (
    <div className="animate-fade-in p-4 md:p-6 space-y-4 md:space-y-5 max-w-[1600px] mx-auto">
      {/* Header */}
      <header className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-3" data-testid="dashboard-header">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            {t("dash.hello", { name: user?.name?.split(" ")[0] || "Agent" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("dash.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter range={range} from={from} to={to} onChange={setRange} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                className="h-9 gap-1.5 px-4 bg-athlynx hover:bg-athlynx-hover text-white shadow-sm"
                data-testid="cta-add-player"
              >
                <Plus className="h-4 w-4" /> {t("dash.addAthlete")} <ChevronDown className="h-3.5 w-3.5 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem onClick={() => setAddOpen(true)} data-testid="add-player-manual" className="gap-2 text-sm">
                <Plus className="h-4 w-4" /> {tf("Ajouter manuellement")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/import")} data-testid="add-player-import" className="gap-2 text-sm">
                <Upload className="h-4 w-4" /> {tf("Importer des athlètes")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/import")}
            className="h-9 text-xs gap-1.5 border-border"
            data-testid="cta-import"
          >
            <Upload className="h-3.5 w-3.5" /> {t("common.import")}
          </Button>
        </div>
      </header>

      {/* Athlete scope context indicator */}
      {athleteMode && <AthleteScopeHeader athlete={athlete} onClear={clearAthlete} />}

      {/* Critical deadline alert — data-driven, rendered only when a genuinely critical deadline exists */}
      {!athleteMode && <CriticalAlertBanner deadlines={deadlines} />}

      {isEmpty && (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 flex flex-col md:flex-row items-center justify-between gap-3" data-testid="dashboard-empty-state">
          <div className="text-sm text-muted-foreground">{t("dash.emptyPipeline")}</div>
          <Button onClick={seed} disabled={seeding} variant="outline" data-testid="seed-demo-btn">
            <Sparkles className="h-4 w-4 mr-2" /> {seeding ? t("dash.loadingDemo") : t("dash.loadDemo")}
          </Button>
        </div>
      )}

      {/* KPI row */}
      {overview?.error ? (
        <WidgetErrorState message={t("dash.kpiError")} onRetry={() => { setOverview(null); loadOverview(); }} testid="kpi-error" />
      ) : !overview ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kpi-skeleton">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[180px] rounded-2xl" />)}
        </div>
      ) : athleteMode ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="athlete-kpi-row">
          <KpiCard
            label={t("dash.kpi.relancer")}
            value={k?.relancer?.total ?? 0}
            subLines={
              (k?.relancer?.total ?? 0) === 0
                ? [tf("Aucune relance en attente")]
                : [t("dash.kpi.todayCount", { n: k?.relancer?.today ?? 0 }), t("dash.kpi.overdueCount", { n: k?.relancer?.overdue ?? 0 })]
            }
            icon={Bell}
            tone="danger"
            spark={k?.relancer?.spark}
            metric="relancer"
            rangeKey={range}
            onClick={() => navigate("/relances?tab=overdue")}
            testid="kpi-athlete-relancer"
          />
          <KpiCard
            label={tf("Statut")}
            value={
              <span className="block text-2xl md:text-[26px] font-semibold leading-tight whitespace-normal break-words">
                {athlete?.status ? t(`status.${athlete.status}`) : "—"}
              </span>
            }
            subLines={[sinceDays !== null && sinceDays !== undefined ? `${tf("Depuis")} ${sinceDays} ${sinceDays > 1 ? tf("jours") : tf("jour")}` : null]}
            icon={Flag}
            tone="warning"
            spark={null}
            rangeKey={range}
            onClick={() => athlete?.status && navigate(`/pipeline?status=${encodeURIComponent(athlete.status)}`)}
            testid="kpi-athlete-status"
          />
          <KpiCard
            label={lang === "en" ? "Next deadline" : "Prochaine échéance"}
            value={
              <span className="block text-2xl md:text-[26px] font-semibold leading-tight whitespace-normal break-words">
                {ndDate || "—"}
              </span>
            }
            subLines={[nd ? nd.label : tf("Aucune échéance à venir")]}
            icon={CalendarClock}
            tone="info"
            spark={null}
            rangeKey={range}
            onClick={() => navigate("/calendar")}
            testid="kpi-athlete-deadline"
          />
          <KpiCard
            label={t("nav.documents")}
            value={docs === null ? "…" : docsAttention.length}
            subLines={[
              docs === null ? null
                : docsAttention.length === 0 ? tf("Aucun document à vérifier")
                : docsExpired > 0 ? `${docsExpired} ${docsExpired > 1 ? tf("expirés") : tf("expiré")}` : tf("à vérifier dans les 30 jours"),
            ]}
            icon={FileWarning}
            tone={docsExpired > 0 ? "danger" : docsAttention.length > 0 ? "warning" : "success"}
            spark={null}
            rangeKey={range}
            onClick={() => navigate("/documents")}
            testid="kpi-athlete-docs"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-row">
          <KpiCard
            compact
            label={t("dash.kpi.relancer")}
            value={k?.relancer?.total ?? 0}
            subLines={[t("dash.kpi.todayCount", { n: k?.relancer?.today ?? 0 }), t("dash.kpi.overdueCount", { n: k?.relancer?.overdue ?? 0 })]}
            icon={Bell}
            tone="danger"
            spark={k?.relancer?.spark}
            metric="relancer"
            rangeKey={range}
            tooltip={tfr("Instantané opérationnel : athlètes uniques dont la prochaine relance est due aujourd'hui ou en retard — identique au badge latéral, à la page À relancer et à Athlynx Intelligence. Indépendant de la période sélectionnée.")}
            onClick={() => navigate("/relances?tab=overdue")}
            testid="kpi-relancer"
          />
          <StageKpiCard stage="Repéré" data={k?.stages?.["Repéré"]} rangeKey={range} rangeLabel={rangeLabel}
            onClick={() => navigate("/reperes")} testid="kpi-stage-repere" />
          <StageKpiCard stage="Contacté" data={k?.stages?.["Contacté"]} rangeKey={range} rangeLabel={rangeLabel}
            onClick={() => navigate("/prises-de-contact")} testid="kpi-stage-contacte" />
          <StageKpiCard stage="En discussion" data={k?.stages?.["En discussion"]} rangeKey={range} rangeLabel={rangeLabel}
            onClick={() => navigate("/discussions-actives")} testid="kpi-stage-discussion" />
          <StageKpiCard stage="Mandat signé" data={k?.stages?.["Mandat signé"]} rangeKey={range} rangeLabel={rangeLabel}
            onClick={() => navigate("/mandats-signes")} testid="kpi-stage-mandat" />
          <StageKpiCard stage="Placé" data={k?.stages?.["Placé"]} rangeKey={range} rangeLabel={rangeLabel}
            onClick={() => navigate("/placements")} testid="kpi-stage-place" />
        </div>
      )}

      {/* Row 2 — Context / decision layer: Intelligence / Activity / Distribution|Journey */}
      {dash?.error ? (
        <WidgetErrorState message={t("dash.dashError")} onRetry={() => { setDash(null); loadDash(); loadDocs(); }} testid="dash-error" />
      ) : !dash ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[340px] rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch w-full min-w-0" data-testid="dashboard-row-2">
          <DashboardIntelligenceCard
            playerId={playerId}
            athleteName={athlete?.name || ""}
            signals={{ relancer: k?.relancer?.total ?? 0, docsCount: docsAttention.length, nextDeadline: !!nd }}
          />
          <ActivityFeedCard
            playerId={playerId}
            onPlayerClick={setOpenPlayer}
            refreshKey={dash}
            title={athleteMode && firstName ? `${tf("Activité de")} ${firstName}` : undefined}
          />
          {athleteMode ? <AthleteJourneyCard athlete={athlete} /> : <UpcomingDeadlinesCard deadlines={deadlines} />}
        </div>
      )}

      {/* Row 3 — Operational / time layer: Calendar / Upcoming / Documents */}
      {dash?.error ? null : !dash ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-[300px] rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch" data-testid="dashboard-row-3">
          <WeeklyCalendarCard
            agenda={dash?.agenda || []}
            onItemClick={(it) => { if (it.player?.id) setOpenPlayer(it.player.id); else navigate("/calendar"); }}
            onAddEvent={() => setEventOpen(true)}
          />
          <UpcomingEventsCard events={dash?.upcoming_events || []} />
          <ExpiringDocumentsCard docs={docs || []} />
        </div>
      )}

      <PlayerSheet
        playerId={openPlayer}
        open={!!openPlayer}
        onOpenChange={(o) => !o && setOpenPlayer(null)}
        onUpdated={reloadAll}
      />
      <AddPlayerDialog open={addOpen} onOpenChange={setAddOpen} onCreated={reloadAll} defaultStatus="Repéré" />
      <EventDialog open={eventOpen} onOpenChange={setEventOpen} onCreated={reloadAll} />
    </div>
  );
}
